import { Button, ButtonVariants } from "@inno-ui/button";
import {
  CalendarLineIcon,
  ChevronLeftIcon,
  ClockLineIcon,
  IdCardLineIcon,
} from "@inno-ui/icons";
import {
  TabItems,
  TabItemsDirections,
  TabItemsVariants,
} from "@inno-ui/tab-items";
import cn from "classnames";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useMultiStepForm } from "../../hooks/use-multi-step-form";
import { useAppointmentStore } from "../../store/appointment/appointment-store";
import { useGeneralStore } from "../../store/general/general-store";
import { AddGuestMobile } from "./add-guest-mobile/add-guest-mobile";
import styles from "./mobile-main.module.scss";
import { AppointmentConfirmationMobile } from "./screens/appointment-confirmation-mobile/appointment-confirmation-mobile";
import { Contact } from "./screens/contact/contact";
import { Dashboard } from "./screens/dashboard/dashboard";
import { Date } from "./screens/date/date";
import { Time } from "./screens/time/time";

type MobileMainProps = {
  isEmbed?: boolean;
};

export function MobileMain({ isEmbed = false }: MobileMainProps) {
  const { t } = useTranslation();
  const {
    resetCurrentSelectedParticipant,
    appointmentData: { date, timeRange },
  } = useAppointmentStore();
  const {
    setOnNext,
    setOnPrev,
    setOnReset,
    isAddingGuest,
    hideAddGuestForm,
    isConfirmationPage,
  } = useGeneralStore();
  const { steps, currentStepIndex, next, prev, goto, reset } = useMultiStepForm([
    <Date key="date" />,
    <Time key="time" isEmbed={isEmbed} />,
    <Contact key="contact" isEmbed={isEmbed} />,
    <AppointmentConfirmationMobile key="confirmation" />,
  ]);

  const tabsContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOnNext(next);
    setOnPrev(prev);
    setOnReset(reset);
  }, [setOnNext, setOnPrev, setOnReset]);

  // On every step change (Next/Back) bring the "Book an Appointment" card to
  // the top so a new step doesn't inherit the previous step's scroll position.
  useEffect(() => {
    if (isEmbed) {
      // embed: the card fills the widget, just reset its inner scroll
      tabsContentRef.current?.scrollTo({ top: 0 });
    }
    else {
      // non-embed: align the card to the top of the page. If the page is short
      // and can't scroll, nothing above it is forced off-screen.
      tabsContentRef.current?.scrollIntoView({ block: "start" });
    }
  }, [currentStepIndex, isEmbed]);

  // Opening the "Add Guest" form should start scrolled at the very top.
  useEffect(() => {
    if (isAddingGuest)
      window.scrollTo({ top: 0 });
  }, [isAddingGuest]);

  return (
    <div className={cn(styles.mobileMain, !isEmbed && styles.mobileMainScrollable)}>
      {/* add guest */}
      {isAddingGuest ? (
        <div className={styles.guestWrapper}>
          <div className={styles.guest}>
            <div>
              <Button
                icon={ChevronLeftIcon}
                variant={ButtonVariants.Ghost}
                onClick={() => {
                  hideAddGuestForm();
                  resetCurrentSelectedParticipant();
                }}
              />
              <h3>{t("add-guest")}</h3>
            </div>
            <AddGuestMobile />
          </div>
        </div>
      ) : (
        <div className={cn(styles.mobileHeaderContent, !isEmbed && styles.mobileHeaderContentScrollable)}>
          {!isConfirmationPage && <Dashboard />}
          {/* normal tabs flow */}
          <div
            ref={tabsContentRef}
            className={cn(
              styles.mobileHeaderTabsContent,
              isConfirmationPage && styles.mobileHeaderTabsContentConfirmation,
              !isEmbed && styles.mobileHeaderTabsContentScrollable,
            )}
          >
            <div className={styles.mobileHeader}>
              {!isConfirmationPage && (
                <>
                  <h3>{t("book-an-appointment")}</h3>
                  <div className={styles.mobileHeaderTabs}>
                    <TabItems
                      direction={TabItemsDirections.Horizontal}
                      variant={TabItemsVariants.Minimal}
                      options={[
                        { label: t("date"), icon: CalendarLineIcon, value: 2 },
                        // Time is unlocked only after a date is picked
                        { label: t("time"), icon: ClockLineIcon, value: 3, disabled: !date },
                        // Contact is unlocked only after a time slot is picked
                        { label: t("contact"), icon: IdCardLineIcon, value: 4, disabled: !timeRange.length },
                      ]}
                      value={currentStepIndex + 2}
                      // Clicking an unlocked tab jumps to that step, just like Back/Next
                      onChange={(value) => {
                        const targetStep = Number(value) - 2;
                        // respect the same locks as the disabled flags above
                        if (targetStep === 1 && !date)
                          return;
                        if (targetStep === 2 && !timeRange.length)
                          return;
                        goto(targetStep);
                      }}
                    />
                  </div>
                </>
              )}
            </div>
            {steps[currentStepIndex]}
          </div>
        </div>
      )}
    </div>
  );
}
